import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import masterPP from '~/scopes/product-core/middleware/masterPP/masterPP'
import prefetchAndValidate from '~/scopes/product-core/middleware/prefetchAndValidate/prefetchAndValidate'
import redirectInvalidUUID from '~/scopes/product-core/middleware/redirectInvalidUUID/redirectInvalidUUID'
import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'
import { OBSOLETE_DRAWER_TYPES } from '~/scopes/product-core/routes.constants'

export default (): RouteRecordRaw[] => {
  const { FF_MODULAR_PDP_ENABLED_BY_COUNTRY } = useRuntimeConfig().public
  const { market } = useMarketplace()

  const isModularPdpEnabled = FF_MODULAR_PDP_ENABLED_BY_COUNTRY.includes(
    market.countryCode,
  )

  const isJapanMarket = market.countryCode === MarketCountryCode.JP

  const japanRoutes: RouteRecordRaw[] = isJapanMarket
    ? [
        {
          component: () =>
            import('~/scopes/product-core/pages/ProductDetailPage/jp/components/presentationals/VerifiedRefurbishedModal/components/VerifiedRefurbishedModalBody/VerifiedRefurbishedModalBody.vue'),
          name: PRODUCT_ROUTES.WEBVIEWS_JP_VERIFIED_REFURBISHED_DRAWER,
          path: 'p/drawers/verified-refurbished',
          meta: {
            layout: false,
            owners: ['bot-squad-global-growth-front'],
            tracking: { contentGroup: 'Product Drawer' },
          },
        },
      ]
    : []

  return [
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductProxy/ProductProxy.vue'),
      name: PRODUCT_ROUTES.VARIANT_PRODUCT_PAGE,
      path: 'p/:slugV2/:uuid',
      meta: {
        middleware: isModularPdpEnabled
          ? [redirectInvalidUUID, prefetchAndValidate]
          : [redirectInvalidUUID],
        owners: ['bot-squad-product-page-front'],
        cache: 'public, max-age=90000, stale-while-revalidate=90000',
        tracking: { contentGroup: 'Product Detail Page' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/pages/ProductProxy/ProductProxy.vue'),
      name: PRODUCT_ROUTES.MASTER_PRODUCT_PAGE,
      path: 'p/:slug',
      meta: {
        middleware: isModularPdpEnabled
          ? [masterPP, prefetchAndValidate]
          : [masterPP],
        owners: ['bot-squad-product-page-front'],
        cache: 'public, max-age=90000, stale-while-revalidate=90000',
        tracking: { contentGroup: 'Product Detail Page' },
      },
    },
    // Webviews
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/VisibleByVerizonDrawer/components/VisibleByVerizonDrawerBody/VisibleByVerizonDrawerBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_VISIBLE_BY_VERIZON_DRAWER,
      path: 'p/drawers/visible-by-verizon',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/MacbookProcessorModal/components/MacbookProcessorModalBody/MacbookProcessorModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_MACBOOK_PROCESSOR_DRAWER,
      path: 'p/drawers/processor/macbook',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/LaptopProcessorModal/components/LaptopProcessorModalBody/LaptopProcessorModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_LAPTOP_PROCESSOR_DRAWER,
      path: 'p/drawers/processor/laptop',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/components/ItemObsoleteSoftwareModal/components/ItemObsoleteSoftwareModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_SOFTWARE_DRAWER,
      path: `p/drawers/${OBSOLETE_DRAWER_TYPES[PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_SOFTWARE_DRAWER]}`,
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/components/ItemObsoleteOSModal/components/ItemObsoleteOSModalBody/ObsoleteOSModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_OS_DRAWER,
      path: `p/drawers/${OBSOLETE_DRAWER_TYPES[PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_OS_DRAWER]}`,
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/Seller/components/SellerDetailsModal/components/SellerDetailsModalBody/SellerDetailsModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_SELLER_DETAILS_DRAWER,
      path: 'p/drawers/seller-details',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    ...japanRoutes,
    /**
     * @deprecated Use WEBVIEWS_MACBOOK_PROCESSOR_DRAWER instead
     */
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/CustomizationFunnel/components/ProcessorModal/components/MacbookProcessorModal/components/MacbookProcessorModalBody/MacbookProcessorModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_PROCESSOR_DRAWER,
      path: 'p/drawers/processor',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
    /**
     * @deprecated Use WEBVIEWS_MACBOOK_PROCESSOR_DRAWER instead
     */
    {
      component: () =>
        import('~/scopes/product-core/components/presentationals/ReassuranceItems/components/ItemObsolete/components/ItemObsoleteOSModal/components/ItemObsoleteOSModalBody/ObsoleteOSModalBody.vue'),
      name: PRODUCT_ROUTES.WEBVIEWS_OBSOLETE_DRAWER,
      path: 'p/drawers/obsolete',
      meta: {
        layout: false,
        owners: ['bot-squad-product-page-front'],
        tracking: { contentGroup: 'Product Drawer' },
      },
    },
  ]
}
